import React, { useRef, useEffect, useState } from 'react';
import { trackSectionView } from '../../firebaseConfig';

// High-quality transparent logos for top companies with light/dark variants
const companies = [
  {
    id: 'google',
    name: 'Google',
    logoLight: 'https://cdn.freebiesupply.com/images/large/2x/google-logo-transparent.png',
    logoDark: 'https://cdn.freebiesupply.com/images/large/2x/google-logo-transparent.png',
  },
  {
    id: 'amazon',
    name: 'Amazon',
    logoLight: 'https://pngimg.com/uploads/amazon/amazon_PNG6.png',
    logoDark: 'https://m.media-amazon.com/images/G/01/iscore/inspire/amazon_logo_RGB_REV._CB1627603987_.png',
  },
  {
    id: 'razorpay',
    name: 'Razorpay',
    logoLight: 'https://upload.wikimedia.org/wikipedia/commons/8/89/Razorpay_logo.svg',
    logoDark: 'https://d6xcmfyh68wv8.cloudfront.net/newsroom-content/uploads/2021/02/white.png',
  },
  {
    id: 'zomato',
    name: 'Zomato',
    logoLight: 'https://brandlogos.net/wp-content/uploads/2025/02/zomato-logo_brandlogos.net_9msh7.png',
    logoDark: 'https://brandlogos.net/wp-content/uploads/2025/02/zomato-logo_brandlogos.net_9msh7.png',
  },

  {
    id: 'groww',
    name: 'Groww',
    logoLight: 'https://upload.wikimedia.org/wikipedia/commons/b/bb/Groww_app_logo.png',
    logoDark: 'https://assets.nextleap.app/image/eyJidWNrZXQiOiJuZXh0bGVhcC1zdGF0aWMtYXNzZXRzIiwia2V5IjoiaW1hZ2VzL2dyb3d3X2xvZ29fZGFyay5wbmciLCJlZGl0cyI6eyJyZXNpemUiOnsid2lkdGgiOjg4NC42MjUsImhlaWdodCI6MjQxLjUsImZpdCI6ImNvdmVyIiwid2l0aG91dEVubGFyZ2VtZW50Ijp0cnVlfSwicm90YXRlIjpudWxsfX0=',
  },
  {
    id: 'atlassian',
    name: 'Atlassian',
    logoLight: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4d/Atlassian-logo.svg/1280px-Atlassian-logo.svg.png',
    logoDark: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4d/Atlassian-logo.svg/1280px-Atlassian-logo.svg.png',
  },
  {
    id: 'kpmg',
    name: 'KPMG',
    logoLight: 'https://upload.wikimedia.org/wikipedia/commons/9/9d/KPMG_logo.svg',
    logoDark: 'https://esgassurancematurityindex.kpmg.com/wp-content/uploads/2023/12/609-6090613_kpmg-white-on-clear-1-kpmg-logo-png.png',
  },
  {
    id: 'swiggy',
    name: 'Swiggy',
    logoLight: 'https://www.cdnlogo.com/logos/s/42/swiggy.svg',
    logoDark: 'https://www.cdnlogo.com/logos/s/42/swiggy.svg',
  },
  {
    id: 'deloitte',
    name: 'Deloitte',
    logoLight: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/56/Deloitte.svg/1200px-Deloitte.svg.png',
    logoDark: 'https://ditto.tv/wp-content/uploads/2019/11/deloitte-logo-black-and-white-1-e1574953463204.png',
  },
  {
    id: 'paytm',
    name: 'Paytm',
    logoLight: 'https://img-cdn.thepublive.com/filters:format(webp)/sambad-english/media/post_attachments/wp-content/uploads/2017/02/Paytm.png',
    logoDark: 'https://companieslogo.com/img/orig/PAYTM.NS_BIG.D-d7c46d27.png?t=1720244493',
  },
];

export function CompanyLogos() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [visibleLogos, setVisibleLogos] = useState<string[]>([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Detect theme changes
  useEffect(() => {
    const checkTheme = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };

    // Initial check
    checkTheme();

    // Set up observer for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.attributeName === 'class' &&
          mutation.target === document.documentElement
        ) {
          checkTheme();
        }
      });
    });

    // Start observing document for class changes
    observer.observe(document.documentElement, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []);
  
  useEffect(() => {
    // Track the section view
    trackSectionView('Company Logos Section');
    
    // Set up intersection observer
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          
          // Staggered animation sequence
          setTimeout(() => setAnimationPhase(1), 100); // Heading
          
          // Reveal logos with staggered timing
          setTimeout(() => {
            const logoRevealInterval = setInterval(() => {
              setVisibleLogos(prev => {
                const newLogos = [...prev];
                const remainingLogos = companies
                  .filter(company => !prev.includes(company.id))
                  .map(company => company.id);
                
                // If all logos are visible, clear the interval
                if (remainingLogos.length === 0) {
                  clearInterval(logoRevealInterval);
                  return prev;
                }
                
                // Add the next logo
                const nextLogo = remainingLogos[0];
                return [...prev, nextLogo];
              });
            }, 100); // 100ms between each logo appearance
            
            // Safety cleanup for the interval
            setTimeout(() => clearInterval(logoRevealInterval), 3000);
          }, 300);
          
          observer.disconnect();
        }
      },
      { threshold: 0.2 } // Trigger when 20% of section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div 
      ref={sectionRef}
      className="py-16 reveal-animation relative"
    >
      {/* Background Effects */}
      <div 
        className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-light/3 to-transparent dark:via-primary-dark/3 opacity-0 transition-opacity duration-1000" 
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      
      <div className="max-w-6xl mx-auto px-4 relative">
        {/* Refined heading with subtle styling and animation */}
        <div className="text-center mb-10">
          <p 
            className={`text-sm tracking-wide uppercase font-medium text-text-secondary-light dark:text-text-secondary-dark opacity-80 transform transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Candidates prepared via MockTrail got placed in these companies
          </p>
        </div>
        
        {/* Clean badge-style logos in grid layout with staggered animation */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-16 gap-y-14 justify-items-center items-center">
          {companies.map((company, index) => (
            <div
              key={company.id}
              className="transition-all duration-500 hover:scale-110 group relative"
              title={`${company.name}`}
              style={{
                opacity: visibleLogos.includes(company.id) ? 1 : 0,
                transform: visibleLogos.includes(company.id) 
                  ? 'translateY(0) scale(1)' 
                  : 'translateY(20px) scale(0.9)',
                transition: `all 0.8s cubic-bezier(0.16,1,0.3,1) ${index * 0.05}s`
              }}
            >
              {/* Subtle glow effect on hover */}
              <div className="absolute inset-0 rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-xl scale-0 group-hover:scale-125 transition-transform duration-500 -z-10"></div>
              
              <img
                src={isDarkMode ? company.logoDark : company.logoLight}
                alt={`${company.name} logo`}
                loading="lazy"
                className={`${company.id === 'zomato' || company.id === 'atlassian' ? 'h-6' : company.id === 'amazon' || company.id === 'razorpay' || (company.id === 'paytm' && isDarkMode) || company.id === 'deloitte' ? 'h-7' : 'h-8'} w-auto object-contain opacity-100 transition-all duration-300 group-hover:filter-none group-hover:opacity-75 dark:brightness-[1.15]`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
} 